import { useState, useEffect } from 'react';

interface TimezoneOption {
  value: string;
  label: string;
}

interface RailsTimezoneData {
  name: string;
  formatted_offset: string;
}

export const useTimezones = (railsTimezones?: RailsTimezoneData[]) => {
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true);

  useEffect(() => {
    if (railsTimezones && Array.isArray(railsTimezones)) {
      // Use Rails timezone data
      const options: TimezoneOption[] = railsTimezones.map((timezone) => ({
        value: timezone.name,
        label: `${timezone.name} (${timezone.formatted_offset})`,
      }));

      setTimezoneOptions(options);
      setIsLoadingTimezones(false);
    } else {
      // Fallback to TimeZoneDB API if no Rails data provided
      const fetchTimezones = async () => {
        setIsLoadingTimezones(true);
        try {
          const apiKey = 'N2IE9BKU2EQR';
          const response = await fetch(`https://api.timezonedb.com/v2.1/list-time-zone?key=${apiKey}&format=json`);
          const data = await response.json();

          if (data.status === 'OK' && data.zones && Array.isArray(data.zones)) {
            const options: TimezoneOption[] = data.zones.map((zone: any) => ({
              value: zone.zoneName,
              label: `${zone.zoneName} (GMT${zone.gmtOffset >= 0 ? '+' : ''}${Math.floor(zone.gmtOffset / 3600)}:${Math.abs(
                (zone.gmtOffset % 3600) / 60,
              )
                .toString()
                .padStart(2, '0')})`,
            }));

            setTimezoneOptions(options);
          } else {
            console.error('Error fetching timezone list:', data.message || 'Unknown error');
          }
        } catch (error) {
          console.error('Failed to fetch timezones:', error);
        } finally {
          setIsLoadingTimezones(false);
        }
      };

      fetchTimezones();
    }
  }, [railsTimezones]);

  return {
    timezoneOptions,
    isLoadingTimezones,
  };
};
